import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar'
import { motion } from 'framer-motion'
import {
  Shield,
  Droplet,
  Home,
  RadioIcon,
  Trophy,
  Flame,
  AlertCircle,
  Download,
  Users,
  LogOut,
  Bell,
  Map,
  Zap,
  Cloud,
  AlertTriangle,
  DollarSign,
  ArrowRight,
  Sparkles
} from 'lucide-react'
import 'react-circular-progressbar/dist/styles.css'
import { useAuth } from '../contexts/AuthContext'
import { useModules, useDailyTasks, useUserBadges, useUserProgress, useCompleteTask } from '../hooks/useAPI'
import XPProgressBar from '../components/XPProgressBar'
import DailyTip from '../components/DailyTip'
import { useChaosMode } from '../components/ChaosMode'
import { useBadgeToast } from '../components/BadgeNotification'
import { APIErrorBoundary } from '../components/ErrorBoundary'
import FamilySync from '../components/FamilySync'
import PushNotifications from '../components/PushNotifications'
import OfflineMode from '../components/OfflineMode'
import IntelligentSearch from '../components/IntelligentSearch'
import Paywall from '../components/Paywall'
import { useSubscription } from '../contexts/SubscriptionContext'
import SectionHeading from '../components/SectionHeading'
import Footer from '../components/Footer'
import '../styles/home.css'

/**
 * HomeScreen Component
 * Main dashboard showing user progress, modules, daily tasks, and quick actions
 */
const HomeScreen = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user, logout } = useAuth()
  const { enabled: chaosModeEnabled, toggleChaosMode } = useChaosMode()
  const { showBadgeToasts } = useBadgeToast()
  const { isPremium } = useSubscription()
  const [showFamilySync, setShowFamilySync] = useState(false)
  const [showNotifications, setShowNotifications] = useState(false)
  const [showOfflineMode, setShowOfflineMode] = useState(false)
  const [showIntelligentSearch, setShowIntelligentSearch] = useState(false)
  const [showPaywall, setShowPaywall] = useState(null) // 'aiSearch', 'familySync', 'offlineMode'
  
  // Get user profile from onboarding data
  const getUserProfile = () => {
    try {
      const onboardingData = localStorage.getItem('onboardingAnswers')
      if (onboardingData) {
        const answers = JSON.parse(onboardingData)
        return {
          skillLevel: answers.preparednessLevel || 'beginner',
          location: answers.primaryConcern === 'outdoor' ? 'wilderness' : 
                   answers.primaryConcern === 'home' ? 'urban' : 'general',
          emergencyConcerns: [answers.primaryConcern || 'general'],
          familySize: answers.familySize || 'solo',
          learningStyle: answers.learningStyle || 'visual'
        }
      }
    } catch (error) {
      console.error('Failed to parse onboarding data:', error)
    }
    return null
  }

  // Fetch data from API using React Query hooks
  const { data: modulesData, isLoading: modulesLoading } = useModules()
  const { data: dailyTasksData, isLoading: tasksLoading } = useDailyTasks()
  const { data: badgesData, isLoading: badgesLoading } = useUserBadges()
  const { data: progressData, isLoading: progressLoading } = useUserProgress()

  // Daily Tasks Mutation
  const completeTaskMutation = useCompleteTask()

  // Extract data from API responses
  const apiModules = modulesData?.modules || []
  const dailyTasks = dailyTasksData?.tasks || []
  const dailyTask = dailyTasks.length > 0 ? dailyTasks[0] : null
  const userBadges = badgesData?.earned || []
  const userProgress = progressData?.completedLessons || []

  // Mock progress data with icons - will be calculated from API later
  const moduleIcons = {
    'Before the Blast': { icon: Shield, color: '#2563eb' },
    'Fallout 101': { icon: RadioIcon, color: '#dc2626' },
    'Safe Shelter Basics': { icon: Home, color: '#16a34a' },
    'Water, Food & First Aid': { icon: Droplet, color: '#0891b2' }
  }

  // Process modules with real progress data
  const modules = apiModules?.map(module => {
    // Find progress for this module
    const moduleProgress = userProgress?.find(p => p.module_id === module.id)
    
    return {
      ...module,
      icon: moduleIcons[module.title]?.icon || Shield,
      color: moduleIcons[module.title]?.color || '#2563eb',
      progress: moduleProgress?.progress_percentage || 0,
      totalLessons: moduleProgress?.total_lessons || 0,
      completedLessons: moduleProgress?.completed_lessons || 0,
    }
  }) || []

  // Process recent achievements (show latest 3 badges)
  const recentBadges = userBadges?.slice(0, 3) || []

  // Calculate overall progress
  const overallProgress = Math.round(
    modules.reduce((acc, mod) => acc + mod.progress, 0) / modules.length
  )

  // Handle daily task completion
  const handleDailyTask = async () => {
    if (dailyTask?.id) {
      await completeTaskMutation.mutateAsync(dailyTask.id)
    }
  }

  return (
    <div className="home-shell">
      <div className="landing-ambient" />

      {/* Header with user info */}
      <header className="home-header">
        <div className="container">
          <motion.div
            className="flex justify-between items-center"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div>
              <h1 className="text-2xl font-bold">Hi, {user?.username} 👋</h1>
              <p className="text-secondary">Ready to survive another day?</p>
            </div>
            <div className="flex items-center gap-md">
              {/* Streak counter */}
              <div className="streak-badge">
                <Flame size={20} />
                <span className="font-bold">{user?.streak_count || 0}</span>
              </div>
              {/* XP Level Badge */}
              <div className="flex items-center gap-2">
                <div className="bg-gradient-to-r from-orange-500 to-red-500 px-3 py-1 rounded-full">
                  <span className="text-white font-bold text-sm">
                    LV {Math.floor((user?.xp || 0) / 150) + 1}
                  </span>
                </div>
              </div>
              {/* Logout button */}
              <button
                onClick={logout}
                className="btn btn-secondary"
                aria-label="Logout"
              >
                <LogOut size={16} />
              </button>
            </div>
          </motion.div>
        </div>
      </header>

      <main className="container mt-lg home-main">
        {/* XP Progress Bar */}
        <motion.div
          className="mb-lg"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <XPProgressBar
            currentXP={user?.xp || 0}
            currentLevel={Math.floor((user?.xp || 0) / 150) + 1}
            className="bg-gray-900 rounded-xl p-4"
          />
        </motion.div>

        {/* Daily Tip */}
        <motion.div
          className="mb-lg"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
        >
          <DailyTip />
        </motion.div>

        {/* Disaster Paths - Main Feature */}
        <motion.section
          className="mb-lg"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <SectionHeading
            title="Disaster Paths"
            actionLabel="View All"
            onAction={() => navigate('/paths')}
          />
          <div className="card paths-feature-card p-lg">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-md mb-md">
              {[
                { icon: RadioIcon, label: 'Nuclear', color: '#dc2626' },
                { icon: AlertTriangle, label: 'Pandemic', color: '#7c3aed' },
                { icon: Zap, label: 'Grid Down', color: '#f59e0b' },
                { icon: Users, label: 'Civil Unrest', color: '#3b82f6' },
                { icon: DollarSign, label: 'Economic', color: '#10b981' },
              ].map((path, index) => (
                <motion.div
                  key={path.label}
                  className="text-center path-icon-item"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.3 + index * 0.05 }}
                  whileHover={{ y: -4, scale: 1.05 }}
                >
                  <div
                    className="w-12 h-12 rounded-xl mx-auto mb-2 flex items-center justify-center"
                    style={{ backgroundColor: `${path.color}20` }}
                  >
                    <path.icon size={24} style={{ color: path.color }} />
                  </div>
                  <span className="text-xs text-gray-300">{path.label}</span>
                </motion.div>
              ))}
            </div>
            <div className="text-center">
              <p className="text-gray-400 mb-3 text-sm">
                250+ interactive lessons across 5 disaster scenarios
              </p>
              <motion.button
                onClick={() => navigate('/paths')}
                className="btn bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 py-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Map size={18} className="inline mr-2" />
                Explore Paths
                <ArrowRight size={16} className="inline ml-1" />
              </motion.button>
            </div>
          </div>
        </motion.section>

        {/* Viral Personality Quiz CTA */}
        <motion.div
          className="mb-lg"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
        >
          <div className="card quiz-cta-card p-lg text-white">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold mb-2">
                  What's Your Survival Personality?
                </h2>
                <p className="text-white/90 mb-3">
                  Urban Cockroach? Doomsday Prepper? Find out in 2 minutes!
                </p>
                <motion.button
                  onClick={() => navigate('/personality-quiz')}
                  className="btn bg-white text-orange-500 hover:bg-gray-100"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Sparkles size={16} />
                  Take the Quiz
                  <ArrowRight size={16} />
                </motion.button>
              </div>
              <div className="hidden md:block quiz-cta-icon">
                <Sparkles size={48} />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Quick Actions Dashboard */}
        {!tasksLoading && dailyTask?.task && !dailyTask?.completed && (
          <div className="card mb-lg daily-task-card">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="font-medium mb-sm">Today's Prep Task</h3>
                <p className="text-secondary">{dailyTask.task.title}</p>
                {dailyTask.task.description && (
                  <p className="text-sm text-secondary mt-1">{dailyTask.task.description}</p>
                )}
              </div>
              <button
                className="btn btn-success"
                onClick={handleDailyTask}
                disabled={completeTaskMutation.isPending}
              >
                {completeTaskMutation.isPending ? 'Completing...' : `Complete (+${dailyTask.task.xp_reward} XP)`}
              </button>
            </div>
          </div>
        )}
        
        {!tasksLoading && dailyTask?.completed && (
          <div className="card mb-lg">
            <div className="text-center p-lg">
              <h3 className="font-medium mb-sm">🎉 All Daily Tasks Complete!</h3>
              <p className="text-secondary">Great job! Check back tomorrow for new tasks.</p>
            </div>
          </div>
        )}

        {/* Overall Progress */}
        <div className="card mb-lg">
          <h2 className="text-xl font-bold mb-md">Your Progress</h2>
          <div className="progress-overview">
            <div style={{ width: 120, height: 120, margin: '0 auto' }}>
              <CircularProgressbar
                value={overallProgress}
                text={`${overallProgress}%`}
                styles={buildStyles({
                  textColor: 'var(--text-primary)',
                  pathColor: 'var(--secondary)',
                  trailColor: 'var(--border)'
                })}
              />
            </div>
            <p className="text-center mt-md text-secondary">
              Overall Preparedness Level
            </p>
          </div>
        </div>

        {/* Learning Modules */}
        <section className="mb-lg">
          <h2 className="text-xl font-bold mb-md">Learning Modules</h2>
          <APIErrorBoundary onRetry={() => window.location.reload()}>
            {modulesLoading || progressLoading ? (
            <div className="grid grid-2 gap-md">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="card module-card">
                  <div className="flex items-center gap-md mb-md">
                    <div className="module-icon" style={{ backgroundColor: '#f3f4f6' }}>
                      <div className="spinner" />
                    </div>
                    <div className="flex-1">
                      <div style={{ height: '20px', backgroundColor: '#f3f4f6', borderRadius: '4px', marginBottom: '8px' }} />
                      <div style={{ height: '16px', backgroundColor: '#f3f4f6', borderRadius: '4px', width: '60%' }} />
                    </div>
                  </div>
                  <div className="progress-bar">
                    <div className="progress-bar-fill" style={{ width: '0%' }} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-2 gap-md">
              {modules.map((module) => (
                <div 
                  key={module.id}
                  className="card module-card"
                  onClick={() => navigate(`/module/${module.id}`)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="flex items-center gap-md mb-md">
                    <div 
                      className="module-icon"
                      style={{ backgroundColor: `${module.color}20` }}
                    >
                      {React.createElement(module.icon, { size: 24, color: module.color })}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-medium">{module.title}</h3>
                      <p className="text-sm text-secondary">
                        {module.completedLessons}/{module.totalLessons} lessons
                      </p>
                    </div>
                  </div>
                  <div className="progress-bar">
                    <div 
                      className="progress-bar-fill"
                      style={{ 
                        width: `${module.progress}%`,
                        backgroundColor: module.color 
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
          </APIErrorBoundary>
        </section>

        {/* Quick Actions */}
        <section className="mb-lg">
          <h2 className="text-xl font-bold mb-md">Quick Actions</h2>
          <div className="grid grid-4 gap-md">
            <button
              className="card quick-action bg-gradient-to-br from-green-500 to-green-600 text-white relative"
              onClick={() => isPremium() ? setShowIntelligentSearch(true) : setShowPaywall('aiSearch')}
            >
              {!isPremium() && <span className="premium-badge">PRO</span>}
              <Shield size={24} />
              <span>AI Search</span>
            </button>
            <button
              className="card quick-action"
              onClick={() => navigate('/simulations')}
            >
              <AlertCircle size={24} />
              <span>Simulation Hub</span>
            </button>
            <button
              className="card quick-action"
              onClick={() => navigate('/badges')}
            >
              <Trophy size={24} />
              <span>My Badges</span>
            </button>
            <button
              className="card quick-action"
              onClick={() => setShowNotifications(true)}
            >
              <Bell size={24} />
              <span>Notifications</span>
            </button>
            <button
              className="card quick-action relative"
              onClick={() => isPremium() ? setShowOfflineMode(true) : setShowPaywall('offlineMode')}
            >
              {!isPremium() && <span className="premium-badge">PRO</span>}
              <Download size={24} />
              <span>Offline Mode</span>
            </button>
          </div>
        </section>

        {/* Settings Section */}
        <section className="mb-lg">
          <h2 className="text-xl font-bold mb-md">Settings</h2>
          <div className="card p-md">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-orange-500" />
                  Chaos Mode
                </h3>
                <p className="text-sm text-secondary mt-1">
                  Random emergency alerts to test your readiness
                </p>
              </div>
              <button
                onClick={toggleChaosMode}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  chaosModeEnabled ? 'bg-orange-500' : 'bg-gray-600'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    chaosModeEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        </section>

        {/* Recent Achievements */}
        {recentBadges.length > 0 && (
          <section className="mb-lg">
            <h2 className="text-xl font-bold mb-md">Recent Achievements</h2>
            <div className="flex gap-md">
              {recentBadges.map((badge) => (
                <div key={badge.id} className="badge-preview">
                  <span className="badge-icon">{badge.emoji || '🏆'}</span>
                  <span className="text-sm">{badge.name}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Family Sync */}
        <section className="mb-lg">
          <div className="card family-sync">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-md">
                <Users size={24} />
                <div>
                  <h3 className="font-medium">
                    Family Sync
                    {!isPremium() && <span className="ml-2 text-xs bg-yellow-500 text-black px-2 py-0.5 rounded">PRO</span>}
                  </h3>
                  <p className="text-sm text-secondary">
                    Connect with family, compete in challenges, and share your survival journey
                  </p>
                </div>
              </div>
              <button
                className="btn btn-secondary"
                onClick={() => isPremium() ? setShowFamilySync(true) : setShowPaywall('familySync')}
              >
                {isPremium() ? 'Connect Family' : 'Upgrade'}
              </button>
            </div>
          </div>
        </section>
      </main>

      {/* Family Sync Modal */}
      {showFamilySync && (
        <FamilySync
          userId={user?.id}
          onClose={() => setShowFamilySync(false)}
        />
      )}

      {/* Push Notifications Modal */}
      {showNotifications && (
        <PushNotifications
          onClose={() => setShowNotifications(false)}
        />
      )}

      {/* Offline Mode Modal */}
      {showOfflineMode && (
        <OfflineMode
          onClose={() => setShowOfflineMode(false)}
        />
      )}

      {showIntelligentSearch && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
                <Shield className="w-8 h-8 text-green-600" />
                AI-Powered Survival Intelligence
              </h2>
              <button
                onClick={() => setShowIntelligentSearch(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <LogOut className="w-6 h-6 text-gray-600" />
              </button>
            </div>
            <div className="overflow-y-auto max-h-[calc(90vh-140px)]">
              <IntelligentSearch userProfile={getUserProfile()} />
            </div>
          </div>
        </div>
      )}

      {/* Paywall Modals */}
      {showPaywall && (
        <Paywall
          feature={showPaywall}
          onClose={() => setShowPaywall(null)}
        />
      )}

      <Footer />
    </div>
  )
}

export default HomeScreen
